/**
 * Iter 203.3 — DropLayer: renderiza el Drop autorizado por backend
 * cruzando la pantalla. Tocarlo dispara el claim atómico server-side:
 * el premio NUNCA se acredita optimistamente — solo tras confirmación.
 */
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { dropTransport } from '@/lib/dropTransport';
import { dropsApi } from '@/lib/dropsApi';
import { Z } from '@/lib/zLayers';
import './drops.css';
import { tr } from '@/contexts/LanguageContext';

const SIZE_PX = { sm: 54, md: 68, lg: 84 };

const rewardLabel = (reward) => {
  if (!reward) return 'Recompensa especial';
  if (reward.type === 'tokens') return `+${reward.amount} Ziyup Tokens`;
  if (reward.type === 'credits') return `+${reward.amount} Ziyup Credits`;
  return reward.label || 'Recompensa especial';
};

export const DropLayer = () => {
  const [drop, setDrop] = useState(null);
  const [phase, setPhase] = useState(null); // crossing | catching | caught
  const [result, setResult] = useState(null);
  const [topPct, setTopPct] = useState(24);
  const missTimer = useRef(null);
  const busyRef = useRef(false);
  const dropRef = useRef(null);
  dropRef.current = drop;

  useEffect(() => {
    const unsub = dropTransport.subscribe((d) => {
      setDrop(d);
      setPhase('crossing');
      setResult(null);
      setTopPct(15 + Math.random() * 28);
      dropsApi.rendered(d.drop_event_id).catch(() => {});
      const durMs = Math.max(4, d.visible_duration_seconds || 6) * 1000;
      missTimer.current = setTimeout(() => {
        dropsApi.miss(d.drop_event_id).catch(() => {});
        setDrop(null);
        setPhase(null);
        dropTransport.resolveDrop();
      }, durMs + 500);
    });
    return () => {
      unsub();
      clearTimeout(missTimer.current);
    };
  }, []);

  const finish = () => {
    setDrop(null);
    setPhase(null);
    dropTransport.resolveDrop();
  };

  const onCatch = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const d = dropRef.current;
    if (busyRef.current || !d || phase !== 'crossing') return;
    busyRef.current = true;
    clearTimeout(missTimer.current);
    setPhase('catching');
    try {
      const res = await dropsApi.catch(d.drop_event_id);
      if (res?.caught) {
        setResult(res);
        setPhase('caught');
        if (res.reward?.type === 'tokens') {
          window.dispatchEvent(new Event('lineage_tokens_updated'));
        }
        setTimeout(() => { setResult(null); finish(); }, 4200);
      } else {
        toast.error(tr("Se escapó… este drop ya no estaba disponible."));
        finish();
      }
    } catch {
      toast.error(tr("No pudimos validar la captura. Intenta con el próximo."));
      finish();
    } finally {
      busyRef.current = false;
    }
  };

  if (!drop) return null;

  const size = SIZE_PX[drop.size] || SIZE_PX.md;
  const durS = Math.max(4, drop.visible_duration_seconds || 6);
  const rtl = drop.direction === 'right_to_left';

  return (
    <div
      className={`fixed inset-0 ${Z.DROP_LAYER} pointer-events-none overflow-hidden`}
      data-testid="drop-layer"
    >
      {phase !== 'caught' && (
        <button
          type="button"
          onPointerDown={onCatch}
          className={`drop-flyer pointer-events-auto ${
            rtl ? 'drop-cross-rtl' : 'drop-cross-ltr'
          } ${phase === 'catching' ? 'drop-flyer-catching' : ''}`}
          style={{ top: `${topPct}%`, animationDuration: `${durS}s` }}
          aria-label={`Atrapar ${drop.public_name || 'drop'}`}
          data-testid={`drop-item-${drop.drop_event_id}`}
        >
          <span className="drop-flyer-bob">
            <span
              className="drop-flyer-orb"
              style={{ width: size, height: size, fontSize: size * 0.52 }}
            >
              {drop.icon || '🎁'}
            </span>
            <span className="drop-flyer-label" data-testid="drop-cta-label">
              {drop.cta_label || tr("¡Tócalo!")}
            </span>
          </span>
        </button>
      )}

      {phase === 'caught' && result && (
        <div
          className="absolute inset-0 flex items-center justify-center pointer-events-auto bg-black/55 backdrop-blur-[3px]"
          onClick={() => { setResult(null); finish(); }}
          data-testid="drop-caught-card"
        >
          <div className="drop-caught-card relative mx-6 max-w-xs w-full rounded-3xl border border-amber-500/40 bg-[#0B0B0D] p-6 text-center shadow-[0_0_60px_-10px_rgba(245,180,60,0.5)]">
            <div className="text-5xl mb-2">{drop.icon || '🎁'}</div>
            <p className="text-[10px] uppercase tracking-widest text-amber-400 font-semibold" data-testid="drop-caught-message">
              {result.message || drop.caught_message || tr("¡Lo atrapaste!")}
            </p>
            <p className="text-lg font-bold text-white mt-1">
              {result.public_name || drop.public_name}
            </p>
            <p
              className="mt-2 inline-block rounded-full bg-amber-500/15 border border-amber-500/40 px-4 py-1.5 font-mono text-sm text-amber-200"
              data-testid="drop-reward-amount"
            >
              {rewardLabel(result.reward)}
            </p>
            <p className="text-[11px] text-white/45 mt-3">{tr("Toca para continuar")}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DropLayer;
